<script setup>
import Layout from "../../layouts/main.vue";
import mushroom from "cem-primary-api";
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { Filter } from "mushroomjs";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const status = ref("loading");
const errorTitle = ref("");
const errorDescription = ref("");
const isConfigError = ref(false);

const redirectToMonitor = async () => {
  status.value = "loading";
  isConfigError.value = false;
  try {
    const pRegion = route.query.p_region;

    if (!pRegion) {
      status.value = "error";
      isConfigError.value = true;
      errorTitle.value = t("t_monitor_config_missing_title");
      errorDescription.value = t("t_monitor_contact_admin");
      return;
    }

    // Kiểm tra user có quyền truy cập region này không
    try {
      const regionUserRes = await mushroom.region_user.getAllAsync(
        {},
        {
          preferRole: "User",
        },
      );
      if (regionUserRes.result) {
        const userRegionIds = regionUserRes.result.map(
          (item) => item.region_id,
        );
        // Lấy region theo code để check id
        const regionCheck = await mushroom.region.listAsync({
          filters: Filter.eq("code", pRegion).build(),
          limit: 1,
        });
        const regionId = regionCheck.result?.[0]?.id;
        if (!regionId || !userRegionIds.includes(regionId)) {
          router.replace("/no-permission");
          return;
        }
      }
    } catch (e) {
      status.value = "error";
      isConfigError.value = true;
      errorTitle.value = t("t_monitor_config_missing_title");
      errorDescription.value = t("t_monitor_contact_admin");
      return;
    }

    // Gọi API regions lọc theo code để lấy monitor_url
    let monitorUrl = "";
    try {
      const regionRes = await mushroom.region.listAsync({
        filters: Filter.eq("code", pRegion).build(),
        limit: 1,
      });
      monitorUrl = regionRes.result?.[0]?.monitor_url;
    } catch (e) {
      console.error("Get region monitor_url error: %o", e);
    }

    if (!monitorUrl) {
      status.value = "error";
      isConfigError.value = true;
      errorTitle.value = t("t_monitor_config_missing_title");
      errorDescription.value = t("t_monitor_contact_admin");
      return;
    }

    // Lấy token
    let token = "";
    try {
      const tokenRes = await mushroom.user.get_integrated_tokenAsync({
        body: { system: "monitor", region_code: pRegion },
      });
      token = tokenRes.result?.token;
    } catch (e) {
      console.error("Get integrated token error: %o", e);
    }
    if (!token) {
      status.value = "error";
      errorTitle.value = t("t_monitor_token_error_title");
      errorDescription.value = t("t_monitor_token_error_desc");
      return;
    }

    // Tạo URL điều hướng
    let redirectUrl;
    try {
      const url = new URL(monitorUrl);
      if (url.searchParams.has("token")) {
        url.searchParams.set("token", token);
      } else {
        url.searchParams.append("token", token);
      }
      redirectUrl = url.toString();
    } catch {
      const separator = monitorUrl.includes("?") ? "&" : "?";
      redirectUrl = `${monitorUrl}${separator}token=${token}`;
    }
    window.location.replace(redirectUrl);
  } catch (e) {
    console.error(e);
    status.value = "error";
    errorTitle.value = t("t_monitor_redirect_error_title");
    errorDescription.value = t("t_monitor_redirect_error_desc");
  }
};

onMounted(() => {
  redirectToMonitor();
});

watch(
  () => route.query.p_region,
  () => {
    redirectToMonitor();
  },
);
</script>

<template>
  <Layout>
    <div class="monitor-page">
      <div v-if="status === 'loading'" class="loading-wrapper">
        <div class="spinner"></div>
        <span>{{ $t("t_loading_monitor") }}</span>
      </div>
      <div v-else class="error-wrapper">
        <div class="error-icon">
          <i class="ri-error-warning-line"></i>
        </div>
        <h3 class="error-title">{{ errorTitle }}</h3>
        <p class="error-desc">{{ errorDescription }}</p>
        <button
          v-if="!isConfigError"
          class="btn btn-soft-secondary btn-border"
          @click="redirectToMonitor"
        >
          <i class="ri-refresh-line me-1"></i>{{ $t("t_retry") }}
        </button>
      </div>
    </div>
  </Layout>
</template>

<style scoped lang="scss">
.monitor-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 200px);
}

.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #64748b;
  font-size: 14px;
}

.error-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  padding: 32px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  max-width: 420px;
  width: 100%;

  button {
    display: flex;
    align-items: center;
    margin-top: 8px;
  }
}

.error-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--vz-danger);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.error-icon i {
  font-size: 28px;
  color: var(--vz-light);
}

.error-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.error-desc {
  font-size: 14px;
  font-weight: 400;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--vz-secondary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
